import type { ChatMessage } from '.';
import { useChatStore } from '.';

const getChatLastMessageId = function () {
  const { messages } = useChatStore.getState();
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role === 'user') {
      return message.messageId;
    }
  }
  return '';
};

/**
 * 寻找被查找message的下一条消息
 * @param message 需要被查找的message
 */
const getNextMessage = function (message: ChatMessage): ChatMessage | null {
  const { messages } = useChatStore.getState();
  // TODO: 目前message没有唯一id，只能用引用来查找，但是这种不可靠，未来需要给message加上唯一id并重构
  const index = messages.findIndex(item => item === message);

  if (index === -1) {
    return null;
  }

  const next = messages[index + 1] || null;
  return next;
};

/**
 * 是否是一轮对话的最后一条消息
 * 判断规则:
 * 1. 一轮对话最后一条信息role必定是agent
 * 2. 一轮对话最后一条信息的下一条信息role必定是user或者没有消息
 * 缓存：使用WeakMap，保证不阻止自动GC回收，当zustand reset时，可进行自动GC
 */
const CacheGetIsLastMessageAtTrace = new WeakMap<ChatMessage, boolean>();
function getIsLastMessageAtTrace(message: ChatMessage): boolean {
  // 缓存优化，避免重复计算
  if (CacheGetIsLastMessageAtTrace.has(message)) {
    return CacheGetIsLastMessageAtTrace.get(message)!;
  }

  const next = getNextMessage(message);
  const r = message.role === 'agent' && (!next || next.role === 'user');

  /**
   * 可缓存
   * 1. 下一个消息是user的情况
   * 2. 返回结果是 false
   * 不可缓存
   * 1. 下一个消息为undefined，会因为新增消息导致缓存失效
   */
  if (!r || next?.role === 'user') {
    CacheGetIsLastMessageAtTrace.set(message, r);
  }
  return r;
}

function getIsLastMessageAtTraceInMessages(messages: ChatMessage[], message: ChatMessage): boolean {
  const index = messages.findIndex(item => item === message);
  const next = index === -1 ? null : messages[index + 1] || null;
  return message.role === 'agent' && (!next || next.role === 'user');
}

/**
 * 所有消息的最后一条消息
 * 判断规则:
 * 1. 一轮对话最后一条信息role必定是agent
 * 2. 一轮对话最后一条信息的下一条信必定没有消息
 */
function getIsLastMessage(message: ChatMessage): boolean {
  if (message.role !== 'agent') {
    return false;
  }
  const next = getNextMessage(message);
  return !next;
}

/**
 * 判断指定 messageId 的 agent 消息是否属于最后一轮 AI 回复
 */
function isInLastAIRound(messages: ChatMessage[], messageId: string): boolean {
  const agentMsgIndex = messages.findIndex(msg => msg.messageId === messageId && msg.role === 'agent');
  if (agentMsgIndex === -1) {
    return false;
  }

  return !messages.slice(agentMsgIndex + 1).some(msg => msg.role === 'user');
}

export {
  getChatLastMessageId,
  getIsLastMessage,
  getIsLastMessageAtTrace,
  getIsLastMessageAtTraceInMessages,
  isInLastAIRound,
};
